import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { Button } from "@learn-me/ui";
import { Copy, ExternalLink, Linkedin, Share2, Trophy, X } from "lucide-react";
import toast from "react-hot-toast";
import { requestCertificate, type Certificate } from "@/features/certificates";
import { getErrorMessage } from "@/lib/api";

interface CourseCompleteCelebrationProps {
  courseId: string;
  courseTitle: string;
  onClose: () => void;
  isOpen: boolean;
}

export function CourseCompleteCelebration({
  courseId,
  courseTitle,
  onClose,
  isOpen,
}: CourseCompleteCelebrationProps) {
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [isPreparingShare, setIsPreparingShare] = useState(false);

  const publicAccreditationUrl = useMemo(() => {
    if (!certificate) return null;
    return `${window.location.origin}/verify?code=${encodeURIComponent(certificate.code)}`;
  }, [certificate]);

  useEffect(() => {
    if (isOpen) {
      // Celebracion epica de confetti
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = {
        startVelocity: 30,
        spread: 360,
        ticks: 60,
        zIndex: 100,
      };

      const randomInRange = (min: number, max: number) =>
        Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50 * (timeLeft / duration);

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ["#f59e0b", "#10b981", "#3b82f6", "#8b5cf6"],
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ["#f59e0b", "#10b981", "#3b82f6", "#8b5cf6"],
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const ensureCertificate = async () => {
    if (certificate) return certificate;

    setIsPreparingShare(true);
    try {
      const issuedCertificate = await requestCertificate(courseId);
      setCertificate(issuedCertificate);
      return issuedCertificate;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setIsPreparingShare(false);
    }
  };

  const getSharePayload = async () => {
    const issuedCertificate = await ensureCertificate();
    if (!issuedCertificate) return null;

    const url = `${window.location.origin}/verify?code=${encodeURIComponent(
      issuedCertificate.code,
    )}`;
    const text = `Completé "${courseTitle}" en ARCUS Learning. Puedes verificar mi título de completitud aquí: ${url}`;

    return {
      title: "Título de completitud",
      text,
      url,
    };
  };

  const handleShare = async () => {
    const payload = await getSharePayload();
    if (!payload) return;

    if (navigator.share) {
      try {
        await navigator.share(payload);
      } catch {
        // Usuario cancelo o error
      }
    } else {
      await navigator.clipboard.writeText(payload.url);
      toast.success("Link público copiado");
    }
  };

  const handleCopyLink = async () => {
    const payload = await getSharePayload();
    if (!payload) return;
    await navigator.clipboard.writeText(payload.url);
    toast.success("Link público copiado");
  };

  const openSocialShare = async (network: "facebook" | "linkedin") => {
    const payload = await getSharePayload();
    if (!payload) return;

    const shareUrl =
      network === "facebook"
        ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(payload.url)}`
        : `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(payload.url)}`;

    window.open(shareUrl, "_blank", "noopener,noreferrer,width=720,height=640");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 15 }}
            className="relative bg-gradient-to-br from-amber-50 via-white to-orange-50 rounded-3xl p-8 max-w-md mx-4 text-center shadow-2xl"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-200"
            >
              <Trophy className="w-12 h-12 text-white" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Felicitaciones
              </h2>
              <p className="text-lg text-amber-600 font-semibold mb-4">
                Has completado el curso
              </p>
              <p className="text-xl text-gray-700 font-medium mb-6">
                {courseTitle}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col gap-3"
            >
              <Button
                onClick={handleShare}
                variant="outline"
                className="w-full"
                disabled={isPreparingShare}
              >
                <Share2 className="w-4 h-4 mr-2" />
                {isPreparingShare ? "Preparando título..." : "Compartir logro"}
              </Button>
              {publicAccreditationUrl && (
                <a
                  href={publicAccreditationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center justify-center gap-1"
                >
                  Ver título público
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyLink}
                  disabled={isPreparingShare}
                  aria-label="Copiar link público"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openSocialShare("facebook")}
                  disabled={isPreparingShare}
                  aria-label="Compartir en Facebook"
                >
                  f
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openSocialShare("linkedin")}
                  disabled={isPreparingShare}
                  aria-label="Compartir en LinkedIn"
                >
                  <Linkedin className="h-4 w-4" />
                </Button>
              </div>
              <Link to="/my-courses" className="w-full">
                <Button className="w-full">Ver mis cursos</Button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
